import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  createOrganization,
  getOrganizationById,
  updateOrganization,
} from '../Config/organizationService';
import {
  centsToDollarInputString,
  dollarsStringToCents,
} from '../Utils/money';

const EMPTY_FORM = {
  name: '',
  legal_name: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  area: '',
  city: '',
  pincode: '',
  tax_id: '',
  notes: '',
  approval_threshold_dollars: '0.00',
  is_active: true,
  seed_mode: 'mobile',
  seed_mobile: '',
  seed_user_id: '',
  seed_role_label: 'Admin',
};

function OrganizationEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = id && id !== 'new';

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [createdMembership, setCreatedMembership] = useState(
    () => location.state?.membership || null
  );
  const [organizationId, setOrganizationId] = useState(isEditMode ? id : null);

  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const response = await getOrganizationById(id);
        if (cancelled) return;
        if (response.success === false) {
          throw new Error(response.message || 'Organization not found');
        }
        const org = response.data || response;
        setOrganizationId(org.organization_id || id);
        setFormData({
          ...EMPTY_FORM,
          name: org.name || '',
          legal_name: org.legal_name || '',
          email: org.email || '',
          phone: org.phone || '',
          line1: org.line1 || '',
          line2: org.line2 || '',
          area: org.area || '',
          city: org.city || '',
          pincode: org.pincode || '',
          tax_id: org.tax_id || '',
          notes: org.notes || '',
          approval_threshold_dollars: centsToDollarInputString(org.approval_threshold ?? 0),
          is_active: org.is_active !== false,
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.message || 'Failed to load organization');
        navigate('/organizations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const buildBillingPayload = () => {
    const thresholdCents = dollarsStringToCents(formData.approval_threshold_dollars);
    if (Number.isNaN(thresholdCents) || thresholdCents < 0) {
      throw new Error('Approval threshold must be a valid amount (dollars)');
    }

    const payload = {
      name: formData.name.trim(),
      line1: formData.line1.trim(),
      city: formData.city.trim(),
      pincode: formData.pincode.trim(),
      approval_threshold: thresholdCents,
      is_active: Boolean(formData.is_active),
    };

    const optional = [
      'legal_name',
      'email',
      'phone',
      'line2',
      'area',
      'tax_id',
      'notes',
    ];
    optional.forEach((key) => {
      const v = String(formData[key] || '').trim();
      if (v) payload[key] = v;
      else if (isEditMode) payload[key] = null;
    });

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!formData.name.trim() || !formData.line1.trim() || !formData.city.trim() || !formData.pincode.trim()) {
        throw new Error('Name, address line 1, city, and pincode are required');
      }

      const billing = buildBillingPayload();

      if (isEditMode) {
        const response = await updateOrganization(id, billing);
        if (response.success === false) {
          throw new Error(response.message || 'Failed to update organization');
        }
        toast.success(response.message || 'Organization updated');
        const updated = response.data || billing;
        setOrganizationId(updated.organization_id || id);
      } else {
        const payload = { ...billing };
        if (formData.seed_mode === 'user_id') {
          const seedUserId = formData.seed_user_id.trim();
          if (!seedUserId) throw new Error('Seed user ID is required');
          payload.seed_user_id = seedUserId;
        } else {
          const seedMobile = formData.seed_mobile.trim();
          if (!seedMobile) throw new Error('Seed mobile is required');
          payload.seed_mobile = seedMobile;
        }
        if (formData.seed_role_label.trim()) {
          payload.seed_role_label = formData.seed_role_label.trim();
        }

        const response = await createOrganization(payload);
        if (response.success === false) {
          throw new Error(response.message || 'Failed to create organization');
        }
        toast.success(response.message || 'Organization created');
        const org = response.data?.organization || response.data;
        const membership = response.data?.membership || null;
        const newId = org?.organization_id;
        if (newId) {
          navigate(`/organizations/${newId}`, {
            replace: true,
            state: { membership },
          });
        } else {
          setCreatedMembership(membership);
        }
      }
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!isEditMode) return;
    const next = !formData.is_active;
    const label = next ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${label} this organization?`)) return;
    setSaving(true);
    try {
      const response = await updateOrganization(id, { is_active: next });
      if (response.success === false) {
        throw new Error(response.message || `Failed to ${label} organization`);
      }
      setFormData((prev) => ({ ...prev, is_active: next }));
      toast.success(next ? 'Organization activated' : 'Organization deactivated');
    } catch (err) {
      toast.error(err?.message || `Failed to ${label}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <div className="container mx-auto px-4 py-16 text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <button
              type="button"
              onClick={() => navigate('/organizations')}
              className="text-sm text-gray-500 hover:text-gray-800 mb-2"
            >
              ← Back to organizations
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit organization' : 'Create organization'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isEditMode
                ? 'Update billing details, approval threshold, or active status'
                : 'Billing profile for invoices + seed the first portal admin'}
            </p>
          </div>
          {isEditMode && organizationId ? (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/orders?channel=B2B&organization_id=${encodeURIComponent(organizationId)}`
                )
              }
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
            >
              View B2B orders
            </button>
          ) : null}
        </div>

        {organizationId ? (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6 text-sm text-blue-900">
            <div className="font-medium">Organization ID</div>
            <code className="mt-1 block break-all text-xs sm:text-sm">{organizationId}</code>
            <p className="mt-2 text-blue-800/80">
              Share with the customer. Portal login uses OTP on the seed mobile and header{' '}
              <code className="text-xs">X-Organization-Id</code>.
            </p>
          </div>
        ) : null}

        {createdMembership ? (
          <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6 text-sm text-green-900">
            <div className="font-medium">Seed member created</div>
            <p className="mt-1">
              User <code className="text-xs">{createdMembership.user_id}</code>
              {createdMembership.role_label ? ` · ${createdMembership.role_label}` : ''}
            </p>
            {Array.isArray(createdMembership.permissions) ? (
              <p className="mt-1 text-green-800/80">
                Permissions: all B2B ({createdMembership.permissions.length})
              </p>
            ) : null}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing / invoice</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  placeholder="Acme Corp"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Legal name</label>
                <input
                  name="legal_name"
                  value={formData.legal_name}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Address line 1 <span className="text-red-500">*</span>
                </label>
                <input
                  name="line1"
                  value={formData.line1}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address line 2</label>
                <input
                  name="line2"
                  value={formData.line2}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Area</label>
                <input
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Pincode / postal <span className="text-red-500">*</span>
                </label>
                <input
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tax ID</label>
                <input
                  name="tax_id"
                  value={formData.tax_id}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Approval threshold (CAD)
                </label>
                <input
                  name="approval_threshold_dollars"
                  value={formData.approval_threshold_dollars}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  placeholder="500.00"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Spend at or above this amount needs portal APPROVE_SPEND. Use 0 for no gate.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Internal notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input
                  id="is_active"
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-[#EF5E28] focus:ring-[#EF5E28]"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Active (inactive orgs cannot use the portal)
                </label>
              </div>
            </div>
          </section>

          {!isEditMode ? (
            <section className="border-t border-gray-100 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Seed portal admin</h2>
              <p className="text-sm text-gray-500 mb-4">
                First member gets all B2B permissions. Provide either a mobile number or an existing
                user ID.
              </p>
              <div className="flex gap-4 mb-4">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="seed_mode"
                    value="mobile"
                    checked={formData.seed_mode === 'mobile'}
                    onChange={handleChange}
                  />
                  Seed by mobile
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="seed_mode"
                    value="user_id"
                    checked={formData.seed_mode === 'user_id'}
                    onChange={handleChange}
                  />
                  Seed by user ID
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.seed_mode === 'mobile' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Seed mobile <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="seed_mobile"
                      value={formData.seed_mobile}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="4165550199"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Seed user ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="seed_user_id"
                      value={formData.seed_user_id}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="uuid"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role label</label>
                  <input
                    name="seed_role_label"
                    value={formData.seed_role_label}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                    placeholder="Admin"
                  />
                </div>
              </div>
            </section>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-6">
            <div>
              {isEditMode ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleToggleActive}
                  className={`px-4 py-2 rounded border ${
                    formData.is_active
                      ? 'border-red-200 text-red-700 hover:bg-red-50'
                      : 'border-green-200 text-green-700 hover:bg-green-50'
                  } disabled:opacity-50`}
                >
                  {formData.is_active ? 'Deactivate' : 'Activate'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/organizations')}
                  className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="bg-[#EF5E28] text-white px-5 py-2 rounded hover:bg-[#d54f1f] disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditMode ? 'Save changes' : 'Create organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrganizationEdit;
